import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { REQUISITION_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { IRequisitionEntityService } from '../../../../../../interfaces/entities/requisition-entity-service-interface';
import { BusinessProcessEntityService } from '../../../../../../services/settings/businessprocess-entity.service';
import { LoginEntityService } from '../../../../../../services/entity-services/login-entity.service';
import { Log, IShortLogger } from '../../../../../../services/logger.service';

/** Notification context used to identify the forward target step. */
const QUOTA_PURPOSE_CODE = 'QuotaAndCleanUpPolicySettings';
const QUOTA_PURPOSE_NAME = 'Quota And CleanUp Policy Settings';

/** Display metadata for a template that has not yet been marked as tested. */
export interface UntestedTemplate {
  type: 'vm' | 'acc';
  label: string;
  icon: string;
}

@Component({
  selector: 'app-forward-settings',
  templateUrl: './forward-settings.component.html',
  styleUrls: ['./forward-settings.component.css'],
})
export class ForwardSettingsComponent implements OnInit {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;
  /** Whether this class has a VM template resource. */
  @Input() hasVmResource = false;
  /** Whether this class has an ACC template resource. */
  @Input() hasAccResource = false;
  /** True once the VM template has been marked tested (snapshot created). */
  @Input() isVmTested = false;
  /** True once the ACC template has been marked tested (AAD user confirmed). */
  @Input() isAccTested = false;
  /** True once the class Stage has been advanced to Confirm by auto-confirm pipeline. */
  @Input() isClassConfirmed = false;

  /** Emitted when the user clicks the deep-link for the VM Template tab. */
  @Output() goToVmTemplate = new EventEmitter<void>();
  /** Emitted when the user clicks the deep-link for the Account Template tab. */
  @Output() goToAccTemplate = new EventEmitter<void>();

  form!: FormGroup;
  teams: any[] = [];
  users: any[] = [];

  isLoadingTeams = false;
  isSubmitting = false;
  submitSuccess = false;

  /**
   * Display metadata per template type — used to render gate icons and navigation button
   * labels without a switch statement.
   */
  private readonly TEMPLATE_META: Record<'vm' | 'acc', { label: string; icon: string }> = {
    vm:  { label: 'VM Template',      icon: 'computer'    },
    acc: { label: 'Account Template', icon: 'account_box' },
  };

  /** Dispatch table that maps each template type to its deeplink emitter. */
  private get navigationActions(): Record<'vm' | 'acc', () => void> {
    return {
      vm:  () => this.goToVmTemplate.emit(),
      acc: () => this.goToAccTemplate.emit(),
    };
  }

  /**
   * Returns one entry per template type that is present on the class but has not yet
   * been marked as tested. Empty when the class is already confirmed.
   */
  get untestedTemplates(): Array<{ type: 'vm' | 'acc'; label: string; icon: string }> {
    if (this.isClassConfirmed) return [];
    const candidates = [
      { type: 'vm'  as const, present: this.hasVmResource,  tested: this.isVmTested  },
      { type: 'acc' as const, present: this.hasAccResource, tested: this.isAccTested },
    ];
    return candidates
      .filter(c => c.present && !c.tested)
      .map(c => ({ type: c.type, ...this.TEMPLATE_META[c.type] }));
  }

  /** True when at least one template still needs testing done before forwarding. */
  get isGated(): boolean {
    return this.untestedTemplates.length > 0;
  }

  private readonly logger: IShortLogger<ForwardSettingsComponent> = Log.create(ForwardSettingsComponent);

  constructor(
    private readonly fb: FormBuilder,
    private readonly bpService: BusinessProcessEntityService,
    private readonly loginService: LoginEntityService,
    private readonly snackBar: MatSnackBar,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) private readonly requisitionService: IRequisitionEntityService,
  ) {}

  /**
   * Navigates to the settings tab for the given template type.
   * Uses a dispatch table to avoid conditional branching.
   */
  navigateTo(type: 'vm' | 'acc'): void {
    this.navigationActions[type]?.();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      team: ['', Validators.required],
      user: ['', Validators.required],
      remarks: ['', Validators.required],
    });

    // Auto-load teams for the Cloud Settings purpose.
    this.loadTeamsForCloudSettings();

    this.form.get('team')?.valueChanges.subscribe(team => {
      this.users = [];
      this.form.patchValue({ user: '' });
      if (team) {
        this.loginService.usersGetByTeam(team).subscribe({
          next: res => { this.users = res; },
          error: err => this.logger.e('Error loading users for team:', err),
        });
      }
    });
  }

  private loadTeamsForCloudSettings(): void {
    this.isLoadingTeams = true;
    this.bpService.getAllBusinessProcess().subscribe({
      next: (bps: any[]) => {
        const reqBp = bps.find(bp => bp.name === 'Requisitions');
        const activity = reqBp?.activities?.find(
          (a: any) => a.code === QUOTA_PURPOSE_CODE || a.name === QUOTA_PURPOSE_NAME,
        );
        this.teams = activity?.actions?.[0]?.rights?.teams ?? [];
        this.isLoadingTeams = false;
        this.logger.i('Loaded teams for Cloud Settings:', this.teams);
      },
      error: err => {
        this.isLoadingTeams = false;
        this.logger.e('Error loading business processes:', err);
      },
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { team, user, remarks } = this.form.value;
    const selectedTeam = this.teams.find(t => t.team === team);
    const selectedUser = this.users.find(u => u.name === user);

    if (!selectedTeam || !selectedUser) {
      this.snackBar.open('Please select a valid team and user.', 'Dismiss', { duration: 3000 });
      return;
    }

    const movement = {
      requisitionId: this.classEntity?.requisitionId ?? this.classId,
      purpose: QUOTA_PURPOSE_NAME,
      purposeCode: QUOTA_PURPOSE_CODE,
      forwardToTeam: team,
      forwardToTeamId: selectedTeam.teamId,
      forwardToUser: user,
      forwardToUserId: selectedUser.id,
      forwardFromTeamId: localStorage.getItem('teamId') ?? '',
      forwardFromUserId: localStorage.getItem('userId') ?? '',
      forwardFromTeam: localStorage.getItem('team') ?? '',
      forwardFromUser: localStorage.getItem('userName') ?? '',
      remarks,
      forwardedOn: new Date(),
      isActive: true,
      isRead: false,
      isPurposeDone: false,
      readDate: null,
      srNo: 0,
    };

    this.isSubmitting = true;
    this.requisitionService.forwardRequisition(movement).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.form.reset();
        this.snackBar.open('Successfully forwarded to Quota & Cleanup Policies team.', 'Dismiss', { duration: 4000 });
        this.logger.i('Forward to Quota & Cleanup Policies successful for class:', this.classId);
      },
      error: err => {
        this.isSubmitting = false;
        this.logger.e('Forward failed:', err);
        this.snackBar.open(err?.error?.message ?? 'Failed to forward.', 'Dismiss', { duration: 4000 });
      },
    });
  }
}
